import Alpine from "alpinejs";

type RoutePath = "/" | "/terms/terms-of-service" | "/terms/privacy-policy";

const ROUTE_TITLES: Readonly<Record<string, string>> = {
  "/": "병원 근무표·급여·근태 관리 | hereby 메디컬",
  "/terms/terms-of-service": "이용약관 | hereby 메디컬",
  "/terms/privacy-policy": "개인정보처리방침 | hereby 메디컬",
};

/**
 * `?section=` 키 → 문서에서 찾아 내려갈 조항 제목의 일부.
 *
 * 약관·고지는 새 탭으로 열리므로(footer.html) 스토어 상태를 물려받지 못한다 — 목적지를
 * URL 로 실어 보내야 한다. 그렇다고 한글 조항명을 그대로 싣지는 않는다: 주소창과 공유
 * 링크에 퍼센트 인코딩이 그대로 노출되고, 조항 문구가 바뀌면 이미 퍼진 링크가 죽는다.
 * URL 에는 안정적인 ASCII 키만 두고 실제 문구는 여기서 푼다.
 */
const POLICY_SECTIONS: Readonly<Record<string, string>> = {
  // 푸터 「개인정보 수집 고지」 — 상담 폼이 받는 항목이 적힌 조항
  collect: "제2조",
};

function normalizePath(pathname: string): string {
  const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return path || "/";
}

function scrollToTop(): void {
  // ⚠️ 먼저 instant 로 못박는다. html 에 `scroll-behavior: smooth` 가 걸려 있어
  // scrollTop 대입은 애니메이션이 되는데, 푸터(≈7,000px)에서 약관을 열면 그 애니메이션이
  // 1초 넘게 이어지는 사이 문서가 교체되며 취소돼 스크롤이 있던 자리에 그대로 남는다
  // (2026-08-31 확인: 대입 직후 6991 → 0.8초 뒤 611 → 문서 교체로 정지).
  // 아래 staged 재시도들도 같은 이유로 전부 무력화되므로, 여기서 한 프레임에 끝내야 한다.
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });

  // 안쪽 스크롤러(<main> 등)는 smooth 대상이 아니라 대입으로 충분하다
  const scrollElements: Array<Element | null> = [
    document.scrollingElement,
    document.documentElement,
    document.body,
    ...Array.from(document.querySelectorAll<HTMLElement>("main")),
  ];
  for (const scrollElement of scrollElements) {
    if (scrollElement) {
      scrollElement.scrollTop = 0;
      scrollElement.scrollLeft = 0;
    }
  }
}

function resetScrollAfterRender(): void {
  scrollToTop();
  Alpine.nextTick(() => {
    scrollToTop();
    requestAnimationFrame(() => {
      scrollToTop();
      window.setTimeout(scrollToTop, 0);
    });
  });
}

/**
 * 첫 진입 해시(/#pricing)로 직접 스크롤한다.
 *
 * 랜딩 본문은 `x-cloak` + `x-show="isLandingRoute()"` 뒤에 있어, 브라우저가 프래그먼트로
 * 스크롤하려는 시점(파싱 직후)에는 아직 display:none 이다 — 브라우저는 대상을 못 찾고
 * 그냥 넘어가며, Alpine 이 본문을 드러낸 뒤에도 다시 시도하지 않는다. 그래서 직접 옮긴다.
 *
 * behavior 를 "instant" 로 고정하는 이유: html 의 `scroll-behavior: smooth` 가 걸려 있어
 * 기본값이면 최초 진입에 7,000px 를 애니메이션으로 훑고 내려간다.
 * 착지 여백(고정 GNB·트랙 알약 회피)은 CSS 의 `main section[id] { scroll-margin-top }` 이
 * scrollIntoView 에 그대로 적용되므로 여기서 따로 빼지 않는다.
 */
function scrollToHashTarget(): void {
  // getElementById — querySelector 는 "#2025" 같은 해시에서 예외를 던진다
  const id = window.location.hash.slice(1);
  if (!id) {
    return;
  }
  Alpine.nextTick(() => {
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "instant", block: "start" });
    });
  });
}

/**
 * 같은 문서 안에서 해시만 바뀐 이동(앵커 클릭 · 앵커 간 뒤로/앞으로)의 스크롤을 맞춘다.
 *
 * `history.scrollRestoration = "manual"` 이라 뒤로/앞으로 이동 때 브라우저가 위치를
 * 복원해 주지 않는다 — 해시만 바뀌고 화면은 그대로라 뒤로가기가 고장난 것처럼 보인다.
 *
 * behavior 를 지정하지 않는(= "auto") 이유가 둘이다.
 * ① 앞으로 가는 앵커 클릭에도 크롬은 popstate 를 쏜다 — 그때는 브라우저가 이미 같은 지점으로
 *    스크롤하는 중이라, 같은 목적지 · 같은 방식이어야 겹쳐 불려도 표가 나지 않는다.
 * ② "auto" 는 CSS 의 `scroll-behavior` 를 따른다 — 평소엔 smooth, 모션 최소화 설정에서는
 *    main.css 의 `prefers-reduced-motion` 분기가 auto 로 바꿔 두어 즉시 이동한다.
 *    여기서 smooth 를 박으면 그 설정을 JS 가 덮어쓴다.
 */
function syncHashScroll(): void {
  const id = window.location.hash.slice(1);
  if (!id) {
    // 해시 없는 항목(첫 "/" 진입)으로 돌아온 경우 — 최상단이 그 항목의 원래 위치다
    window.scrollTo({ top: 0 });
    return;
  }
  document.getElementById(id)?.scrollIntoView({ block: "start" });
}

/**
 * 처리방침 안의 특정 조항으로 내려보낸다.
 *
 * 방침 본문은 구글 문서 내보내기라 한 문장이 여러 `<span>` 으로 쪼개져 있다 — 원문에
 * id 를 심을 자리가 없고, 마크업을 문자열로 찾아도 걸리지 않는다(`제2조` 조차 raw HTML
 * 에서는 매치되지 않는다). 그래서 태그와 무관하게 합쳐지는 `textContent` 로 훑는다.
 *
 * behavior 를 "instant" 로 두는 이유는 첫 진입 해시 이동과 같다 — html 의
 * `scroll-behavior: smooth` 때문에 기본값이면 문서를 애니메이션으로 훑고 내려간다.
 */
function scrollToPolicySection(needle: string): void {
  Alpine.nextTick(() => {
    requestAnimationFrame(() => {
      const scope = document.querySelector(".privacy-policy-document");
      const hit = scope
        ? Array.from(scope.querySelectorAll<HTMLElement>("p, h1, h2, h3, td, th")).find((el) =>
            el.textContent?.includes(needle),
          )
        : null;
      // 못 찾으면 문서 첫머리 — 엉뚱한 위치에 떨어뜨리느니 위에서 시작하는 편이 낫다
      if (hit) {
        hit.scrollIntoView({ behavior: "instant", block: "start" });
      } else {
        scrollToTop();
      }
    });
  });
}

export function createRouterStore() {
  return {
    path: normalizePath(window.location.pathname),

    /**
     * 이 문서에서 찾아 내려갈 조항. 진입 URL 의 `?section=` 이 심어 두고, 그 문서의
     * x-init 이 enterDocument() 에서 한 번 쓰고 비운다 — 문서가 그려진 뒤라야 찾을 수 있다.
     */
    pendingSection: "",

    init(): void {
      history.scrollRestoration = "manual";
      // 약관·고지는 새 탭으로 열리므로 목적지가 URL 로만 온다(footer.html).
      // 모르는 키는 조용히 무시 — 문서 맨 위에서 시작하면 그만이다.
      const sectionKey = new URLSearchParams(window.location.search).get("section");
      this.pendingSection = (sectionKey && POLICY_SECTIONS[sectionKey]) || "";
      // 제목만 맞추고 스크롤은 건드리지 않는다 — 여기서 최상단으로 되돌리면
      // /#pricing 같은 딥링크가 매번 히어로로 튄다. 약관 페이지는 각자
      // x-init 에서 resetScroll() 을 부르므로 첫 진입 리셋이 필요 없다.
      document.title = ROUTE_TITLES[this.path] ?? ROUTE_TITLES["/"];
      scrollToHashTarget();

      window.addEventListener("popstate", () => {
        const next = normalizePath(window.location.pathname);
        // 같은 문서 안의 해시 이동(#pricing …)에도 크롬은 popstate 를 쏜다.
        // 경로가 그대로인데 syncDocument() 를 타면 브라우저가 방금 한 앵커
        // 스크롤을 라우터가 최상단으로 덮어써 GNB 링크가 통째로 죽는다.
        if (next === this.path) {
          syncHashScroll();
          return;
        }
        this.path = next;
        this.syncDocument();
      });
    },

    navigate(to: RoutePath): void {
      const path = normalizePath(to);
      if (path === this.path) {
        return;
      }
      history.pushState(null, "", to);
      this.path = path;
      this.syncDocument();
    },

    syncDocument(): void {
      document.title = ROUTE_TITLES[this.path] ?? ROUTE_TITLES["/"];
      // 찾아갈 조항이 있으면 여기서 위로 올리지 않는다 — 문서가 아직 안 그려져
      // 조항을 못 찾는 시점이라, 스크롤은 enterDocument() 가 맡는다.
      if (!this.pendingSection) {
        resetScrollAfterRender();
      }
    },

    /**
     * 약관·처리방침 문서가 그려진 직후 그 문서의 x-init 이 부른다.
     * 찾아갈 조항이 있으면 그리로, 없으면 평소처럼 맨 위로 올린다.
     */
    enterDocument(): void {
      const section = this.pendingSection;
      this.pendingSection = "";
      if (section) {
        scrollToPolicySection(section);
        return;
      }
      resetScrollAfterRender();
    },

    resetScroll(): void {
      resetScrollAfterRender();
    },

    isLandingRoute(): boolean {
      return this.path === "/";
    },
  };
}
